import React, { useEffect, useState, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { Outlet } from 'react-router-dom'
import { setAdminInfo, setToken } from '../../store/slices/adminSlices'
import request from '../../utils/request'
import { performLogout, getLogoutRedirectUrl } from '../../utils/logout'

const PrivateRoutes = () => {
    const dispatch = useDispatch()
    const [isValidating, setIsValidating] = useState(true)
    const [isValid, setIsValid] = useState(false)
    const validationIntervalRef = useRef(null)
    const lastValidationRef = useRef(0)

    // Industry standard SAML session validation:
    // 1. Validate on component mount (first access to protected route)
    // 2. Periodic re-validation (every 3 minutes - SAML sessions are longer-lived)
    // 3. Validate on window focus (catches session expiration while user is away)
    // 4. Don't validate on every route change (too aggressive, causes flicker)
    
    useEffect(() => {
        let isMounted = true
        const abortController = new AbortController()

        const validateSamlSession = async (isFirstRun = false) => {
            const now = Date.now()
            if (!isFirstRun && now - lastValidationRef.current < 30000) return
            lastValidationRef.current = now

            try {
                const response = await request.get('/auth/me', {
                    signal: abortController.signal,
                })
                if (abortController.signal.aborted || !isMounted) return

                if (response.data?.user) {
                    const userData = response.data.user
                    const token = response.data?.token
                    // Require real admin profile — reject bare {id, role, isAdmin}
                    const hasValidShape = userData?.email || userData?.workEmail || (userData?.firstName && userData?.lastName)
                    if (!hasValidShape) {
                        throw new Error('User object missing email/name (invalid shape)')
                    }

                    // Update Redux and localStorage with fresh session data
                    dispatch(setAdminInfo(userData))
                    localStorage.setItem('adminInfo', JSON.stringify(userData))

                    // Token in Redux only — not localStorage
                    if (token) {
                        dispatch(setToken(token))
                    }

                    setIsValid(true)
                } else {
                    throw new Error('No user data in response')
                }
            } catch (err) {
                if (abortController.signal.aborted || !isMounted) return
                const isAbort = err?.name === 'AbortError' || err?.code === 'ERR_CANCELED'
                if (isAbort) return

                const status = err?.response?.status
                const isAuthFailure = status === 401 || status === 403 || status === 440
                const isInvalidShape = err?.message?.includes('invalid shape') || err?.message?.includes('No user data')

                if (isAuthFailure || isInvalidShape) {
                    if (process.env.NODE_ENV !== 'production' && isInvalidShape) {
                        console.warn('[PrivateRoutes] Invalid user shape - clearing and redirecting to login')
                    }
                    performLogout(dispatch)
                    localStorage.clear()
                    window.location.href = getLogoutRedirectUrl()
                    return
                }

                setIsValid(false)
            } finally {
                if (isMounted && !abortController.signal.aborted) {
                    setIsValidating(false)
                }
            }
        }

        // First run: wait for /auth/me response before rendering Outlet; no throttle
        validateSamlSession(true)

        validationIntervalRef.current = setInterval(() => {
            validateSamlSession(false)
        }, 3 * 60 * 1000)

        const handleFocus = () => {
            validateSamlSession(false)
        }
        window.addEventListener('focus', handleFocus)

        return () => {
            isMounted = false
            abortController.abort()
            if (validationIntervalRef.current) {
                clearInterval(validationIntervalRef.current)
            }
            window.removeEventListener('focus', handleFocus)
        }
    }, [dispatch])

    // Show loader while validating
    if (isValidating) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="w-8 h-8 border-4 border-gray-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-sm text-gray-500">Validating SAML session...</p>
                </div>
            </div>
        )
    }

    // Auth failed — redirect to login so user can re-authenticate (e.g. SAML)
    if (!isValid) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <p className="text-lg font-medium text-gray-700">Authentication required</p>
                    <p className="text-sm text-gray-500 mt-1 mb-4">Your session could not be validated.</p>
                    <button
                        type="button"
                        onClick={() => { window.location.href = getLogoutRedirectUrl(); }}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                    >
                        Go to login
                    </button>
                </div>
            </div>
        )
    }

    // Auth succeeded — show the requested protected route (e.g. /dashboard, /orderList, etc.)
    return <Outlet />
}

export default PrivateRoutes
